import { BoosterShopPoint } from "../types/boosterShop"

type RawRecord = Record<string, unknown>

const DEFAULT_RADIUS_M = 50

function pickString(value: unknown, fallback = ""): string {
  if (typeof value === "string") {
    const trimmed = value.trim()
    return trimmed || fallback
  }
  if (value != null) {
    const asString = String(value).trim()
    return asString || fallback
  }
  return fallback
}

function pickNullableString(value: unknown): string | null {
  const text = pickString(value)
  return text || null
}

function pickNumber(value: unknown, fallback: number | null = null): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string" && value.trim()) {
    const n = Number(value.trim().replace(",", "."))
    return Number.isFinite(n) ? n : fallback
  }
  return fallback
}

function pickPhotos(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value
    .map((item) => pickString(item))
    .filter(Boolean)
}

function unwrapPayload(data: unknown): RawRecord {
  if (!data || typeof data !== "object" || Array.isArray(data)) return {}
  const obj = data as RawRecord
  const nested = obj.data ?? obj.result ?? obj.payload
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return { ...(nested as RawRecord), ...obj }
  }
  return obj
}

export function normalizeBoosterShopPoint(raw: unknown): BoosterShopPoint | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const item = raw as RawRecord
  const id = item.id ?? item.uuid ?? item.pointId ?? item.point_id
  const lat = pickNumber(item.latitude ?? item.lat)
  const lng = pickNumber(item.longitude ?? item.lng ?? item.lon)
  const cityId = pickNumber(item.cityId ?? item.city_id)
  const priceByn = pickNumber(item.priceByn ?? item.price_byn ?? item.price, 0)
  if (id == null || lat == null || lng == null || cityId == null) return null

  return {
    id: String(id),
    name: pickString(item.name ?? item.title, "Магазин бустеров"),
    address: pickNullableString(item.address),
    description: pickNullableString(item.description),
    latitude: lat,
    longitude: lng,
    priceByn: priceByn ?? 0,
    photos: pickPhotos(item.photos ?? item.images ?? item.gallery),
    cityId,
    radiusMeters:
      pickNumber(
        item.radiusMeters ?? item.radius_meters ?? item.radiusM ?? item.radius,
        DEFAULT_RADIUS_M
      ) ?? DEFAULT_RADIUS_M,
  }
}

export function mapBoosterShopPoints(data: unknown): BoosterShopPoint[] {
  if (typeof data === "string" && data.trim()) {
    try {
      return mapBoosterShopPoints(JSON.parse(data))
    } catch {
      return []
    }
  }
  if (Array.isArray(data)) {
    return data
      .map(normalizeBoosterShopPoint)
      .filter((point): point is BoosterShopPoint => point != null)
  }
  const obj = unwrapPayload(data)
  const list =
    obj.points ?? obj.items ?? obj.shops ?? obj.boosterShops ?? obj.booster_shops
  if (Array.isArray(list)) {
    return list
      .map(normalizeBoosterShopPoint)
      .filter((point): point is BoosterShopPoint => point != null)
  }
  const single = normalizeBoosterShopPoint(obj)
  return single ? [single] : []
}

export function formatBoosterPrice(priceByn: number): string {
  return `${priceByn.toLocaleString("ru-BY", {
    minimumFractionDigits: Number.isInteger(priceByn) ? 0 : 2,
    maximumFractionDigits: 2,
  })} BYN`
}
